import { BaseLayer } from "./BaseLayer";
import { IBaseLayer } from "./IBaseLayer";
import TileLayer from "ol/layer/Tile";
import { Options } from "ol/source/BingMaps";
import BingMaps from 'ol/source/BingMaps';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import TileGrid from 'ol/tilegrid/TileGrid';
import Attribution from 'ol/control/Attribution';
import XYZ from 'ol/source/XYZ';
import * as olProj from 'ol/proj';


export enum BaseLayerType {   
    BingAerial,
    BingAerialWithLabels,
    BingRoad,
    BingOS,
    OSM_ESDM_contours,
    OS_Open_Data,
    OSProvisional_1937to1961,
    OSOneInch_1955To1961,
    OSOneInch_Hills1885
}

export class BaseLayerFactory {

    public GetBaseLayerByType(
        baseLayerType: BaseLayerType,
        bingApiKey: string = "",
        esdmApiKey: string = ""
    ): IBaseLayer {
        let lyr: IBaseLayer = null;

        switch (baseLayerType) {           

            case BaseLayerType.BingAerial:
                lyr = this.BingAerial(bingApiKey, "BingAerial", "Bing Aerial");
                break;

            case BaseLayerType.BingAerialWithLabels:
                lyr = this.BingAerialWithLabels(bingApiKey, "BingAerialWithLabels", "Bing Aerial (with labels)");
                break;

            case BaseLayerType.BingRoad:
                lyr = this.BingRoad(bingApiKey, "RoadOnDemand", "Bing Road");
                break;

            case BaseLayerType.BingOS:
                lyr = this.BingOS(bingApiKey, "OrdnanceSurvey", "Bing Ordnance Survey");
                break;

            case BaseLayerType.OSM_ESDM_contours:
                lyr = this.OSM_ESDM_contours(esdmApiKey, "OSM_ESDM_contours", "OpenStreetMap");
                //lyr = this.OSM("OSM_ESDM_contours", "OpenStreetMap"); 
                break;

            case BaseLayerType.OS_Open_Data:
                lyr = this.OSOpenData("OSOpenData", "Ordnance Survey OpenData");
                break;

            case BaseLayerType.OSProvisional_1937to1961:
                lyr = this.OSProvisional_1937to1961();
                break;

            case BaseLayerType.OSOneInch_1955To1961:
                lyr = this.OSOneInch_1955To1961();
                break;

            case BaseLayerType.OSOneInch_Hills1885:
                lyr = this.OSOneInch_Hills1885();
                break;

            default:
                console.warn(`${baseLayerType} layer not configured`);
                break;
        }

        return lyr;
    }

    protected BingAerial(apiKey: string, ref: string, displayName: string): IBaseLayer {
        let lyr = this.GetBinglayer(apiKey, "Aerial");
        lyr.set("name", "BingAerial", true);
        lyr.setVisible(false);
        return new BaseLayer(ref, displayName, lyr);
    }

    protected BingAerialWithLabels(apiKey: string, ref: string, displayName: string): IBaseLayer {
        let lyr = this.GetBinglayer(apiKey, "AerialWithLabelsOnDemand");
        lyr.set("name", "BingAerialWithLabels", true);
        lyr.setVisible(false);
        return new BaseLayer(ref, displayName, lyr);
    }

    protected BingOS(apiKey: string, ref: string, displayName: string): IBaseLayer {
        let lyr = this.GetBinglayer(apiKey, "OrdnanceSurvey", 17);
        lyr.set("name", "BingOrdnanceSurvey", true);
        lyr.getSource().setAttributions('© Crown Copyright and database right ' + new Date().getFullYear() + '. All rights reserved.Ordnance Survey Licence number 100023706');
        lyr.setVisible(false);
        return new BaseLayer(ref, displayName, lyr);
    }

    protected BingRoad(apiKey: string, ref: string, displayName: string): IBaseLayer {
        let lyr = this.GetBinglayer(apiKey, "RoadOnDemand");
        lyr.set("name", "BingRoad", true);
        lyr.setVisible(false);
        return new BaseLayer(ref, displayName, lyr);
    }

    protected GetBinglayer(apiKey: string, imagerySet: string, maxZoom: number = 0): TileLayer {
        let opts: Options = {
            key: apiKey,
            imagerySet: imagerySet,
            culture: 'en-gb'
        }

        if (maxZoom > 0) {
            opts.maxZoom = maxZoom;
        }

        return new TileLayer({
            source: new BingMaps(opts)
        });
    }

    protected OSM_ESDM_contours(apiKey: string, ref: string, displayName: string): IBaseLayer {
        let lyr = new TileLayer({
            source: new OSM({
                url: `//{a-f}.osm.esdm.co.uk/900913/c/{z}/{x}/{y}.png?APIKEY=${apiKey}`,
                maxZoom: 18,
                attributions: 'Map data from © OpenStreetMap'
            })
        });
        lyr.setVisible(false);        
        lyr.set("name", "Contoured", true);
        return new BaseLayer(ref, displayName, lyr);
    }

    //protected OSM(ref: string, displayName: string): IBaseLayer {
    //    let lyr = new TileLayer({
    //        source: new OSM(),
    //    });
    //    lyr.setVisible(false);
    //    lyr.set("name", "Contoured", true);
    //    return new BaseLayer(ref, displayName, lyr);
    //}

    protected OSOpenData(ref: string, displayName: string): IBaseLayer {
        let lyr = new TileLayer({
            //title: 'Ordnance Survey OpenData',
            //name: 'OpenData',
            //type: 'base',
            visible: false,
            source: new TileWMS({
                urls: ["//geoserver9.esdm.co.uk/OrdnanceSurvey/wms", "//geoserver9a.esdm.co.uk/OrdnanceSurvey/wms", "//geoserver9b.esdm.co.uk/OrdnanceSurvey/wms"],
                params: {
                    LAYERS: 'OrdnanceSurvey:OSOpenData',
                    TILED: 'true',
                    FORMAT: 'image/png8'
                },
                projection: 'EPSG:3857',
                //tileGrid: new TileGrid({
                //    origin: [0, 0],
                //    resolutions: [1000, 500, 250, 100, 50, 25, 10, 5, 2.5, 1, 0.5, 0.25],
                //}),
                attributions: "Contains Ordnance Survey data © Crown copyright and database right 2021"
            })
        });
        lyr.set("name", "OpenData", true);
        return new BaseLayer(ref, displayName, lyr);
    }

    protected OSProvisional_1937to1961(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            //title: "Great Britain - OS 1:25,000, 1937-61",
            source: new XYZ({
                urls: [
                    'https://api.maptiler.com/tiles/uk-osgb25k1937/{z}/{x}/{y}.jpg?key=T2ptlwdbXCHbJ9Si1zo8'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library of Scotland</a>',
            }),
            //type: 'overlay',
            visible: false,
            //keytext: 'View the individual sheets of this OS 1:25,000 mapping by selecting "Find by place" above',
            //key: 'geo.nls.uk/mapdata2/os/25000/key/openlayers.html'
        });
        lyr.set("name", "OS_1_25K_Provisional", true);
        return new BaseLayer("OS25k1937", "OS 1:25,000 1937-1961", lyr);
    }

    protected OSOneInch_1955To1961(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-8.8, 49.8, 1.8, 60.9], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            //title: "Great Britain - OS 1:25,000, 1937-61",
            source: new XYZ({
                urls: [
                    'https://api.maptiler.com/tiles/uk-osgb63k1955/{z}/{x}/{y}.jpg?key=T2ptlwdbXCHbJ9Si1zo8'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library of Scotland</a>',
            }),
            //type: 'overlay',
            visible: false,
            //keytext: 'View the individual sheets of this OS 1:25,000 mapping by selecting "Find by place" above',
            //key: 'geo.nls.uk/mapdata2/os/25000/key/openlayers.html'
        });
        lyr.set("name", "OS_One_Inch_7th_Series", true);
        return new BaseLayer("OS63k1955", "OS One-Inch, 7th series, 1955-61", lyr);
    }
    
    protected OSOneInch_Hills1885(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-8.24274385, 49.76831482, 1.91376739, 61.003313], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            //title: "Great Britain - OS 1:25,000, 1937-61",
            source: new XYZ({
                url: 'https://api.maptiler.com/tiles/uk-osgb63k1885/{z}/{x}/{y}.png?key=T2ptlwdbXCHbJ9Si1zo8',  
                minZoom: 1,
                maxZoom: 18,
                attributions: 'We are very grateful to<a href = "https://www.davidrumsey.com/">David Rumsey</ a > for supporting the scanning and georeferencing of this overlay',
            }),
            //type: 'overlay',
            visible: false,
            //keytext: 'View the individual sheets of this OS 1:25,000 mapping by selecting "Find by place" above',
            //key: 'geo.nls.uk/mapdata2/os/25000/key/openlayers.html'
        });
        lyr.set("name", "OS_One_Inch_GB_Hills", true);
        return new BaseLayer("OS63k1955", "OS One-Inch, 7th series, 1955-61", lyr);
    }
    
}